import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Truck, ShieldCheck, Recycle, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useProducts } from '../hooks/useProducts';
import { ProductCard } from '../components/products';
import { Button, Card, Reveal } from '../components/common';

const HomePage: React.FC = () => {
  const { featuredProducts, isLoading } = useProducts();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slideshowImages = [
    {
      url: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=600&h=500&fit=crop',
      alt: 'Fresh vegetables basket',
      title: 'Farm Fresh Vegetables'
    },
    {
      url: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=600&h=500&fit=crop',
      alt: 'Colorful fresh fruits',
      title: 'Seasonal Fruits'
    },
    {
      url: 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=600&h=500&fit=crop',
      alt: 'Organic grains and cereals',
      title: 'Organic Grains'
    },
    {
      url: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=600&h=500&fit=crop',
      alt: 'Fresh farm eggs',
      title: 'Farm Fresh Eggs'
    }
  ];

  // Auto-play slideshow
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, slideshowImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const features = [
    {
      icon: Leaf,
      title: '100% Organic',
      description: 'All our products are certified organic and sustainably grown',
    },
    {
      icon: Truck,
      title: 'Free Delivery',
      description: 'Free delivery on all orders over $50 within Harare',
    },
    {
      icon: ShieldCheck,
      title: 'Quality Guarantee',
      description: 'Freshness guaranteed or full refund within 24 hours',
    },
    {
      icon: Recycle,
      title: 'Eco-Friendly',
      description: 'Sustainable packaging and zero plastic delivery options',
    },
  ];

  const categories = [
    { id: 'vegetables', name: 'Vegetables', image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=300&fit=crop', count: 24 },
    { id: 'fruits', name: 'Fresh Fruits', image: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=400&h=300&fit=crop', count: 18 },
    { id: 'grains', name: 'Grains & Cereals', image: 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=400&h=300&fit=crop', count: 12 },
    { id: 'dairy', name: 'Dairy Products', image: 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400&h=300&fit=crop', count: 8 },
    { id: 'herbs', name: 'Fresh Herbs', image: 'https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?w=400&h=300&fit=crop', count: 15 },
    { id: 'poultry', name: 'Poultry & Eggs', image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400&h=300&fit=crop', count: 6 },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary transition-all duration-300" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary rounded-full filter blur-3xl animate-float-delayed transition-all duration-300" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Leaf className="w-5 h-5 text-white" />
                <span className="text-white font-medium">Farm Fresh to Your Door</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Fresh From Our
                <span className="block text-yellow-300">Farm to Your Table</span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl">
                Discover the finest selection of organic agricultural products directly from 
                local farms. Fresh, healthy, and sustainably grown.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  variant="secondary"
                  size="lg"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  to="/products"
                  as={Link}
                >
                  Shop Now
                </Button>
<Button
  variant="outline"
  size="lg"
  className="bg-white/10 border-white/30 text-gray-800 hover:bg-white/20 hover:text-white dark:text-white dark:hover:text-white"
  to="/about"
  as={Link}
>
  Learn More
</Button>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 mt-12 justify-center lg:justify-start">
                <div>
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-white/70 text-sm">Happy Customers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">100+</div>
                  <div className="text-white/70 text-sm">Farm Partners</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">50+</div>
                  <div className="text-white/70 text-sm">Products</div>
                </div>
              </div>
            </div>

            {/* Hero Slideshow */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="w-full h-[500px] bg-white/10 backdrop-blur-sm rounded-3xl p-8 overflow-hidden">
                  {/* Slideshow Container */}
                  <div className="relative w-full h-full rounded-2xl shadow-2xl overflow-hidden">
                    {/* Slides */}
                    {slideshowImages.map((image, index) => (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                          index === currentSlide ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                        {/* Image Title Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                          <h3 className="text-white text-xl font-semibold">
                            {image.title}
                          </h3>
                        </div>
                      </div>
                    ))}
                    
                    {/* Navigation Buttons */}
                    <button
                      onClick={prevSlide}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-300"
                      aria-label="Previous slide"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    
                    <button
                      onClick={nextSlide}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-300"
                      aria-label="Next slide"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    
                    {/* Slide Indicators */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {slideshowImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentSlide 
                              ? 'w-8 bg-white' 
                              : 'bg-white/50 hover:bg-white/70'
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <Card className="absolute -left-8 top-1/2 -translate-y-1/2 p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center transition-colors duration-300">
                      <Leaf className="w-6 h-6 text-accent dark:text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-white dark:text-dark-dark">100% Organic</p>
                      <p className="text-sm text-gray-300 dark:text-gray-500">Certified Products</p>
                    </div>
                  </div>
                </Card>
                    <Card className="absolute -right-4 bottom-8 p-4 animate-float-delayed">
                      <div className="flex items-center gap-3">
                        {/* Google G logo */}
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
                          <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="font-semibold text-white dark:text-dark-dark">4.9</span>
                          </div>
                          <p className="text-sm text-gray-300 dark:text-gray-500">Google reviews</p>
                        </div>
                      </div>
                    </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              className="fill-dark-dark dark:fill-gray-50 transition-colors duration-300"
            />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Reveal key={index} animation="slide-up" delay={index * 100}>
                <Card hover className="text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                    <feature.icon className="w-8 h-8 text-accent dark:text-primary transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 dark:text-gray-500 text-sm transition-colors duration-300">
                    {feature.description}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                  Shop by Category
                </h2>
                <p className="text-gray-300 dark:text-gray-500 max-w-xl transition-colors duration-300">
                  Browse our wide selection of fresh agricultural products organized by category
                </p>
              </div>
              <Button
                variant="ghost"
                rightIcon={<ArrowRight className="w-5 h-5" />}
                to="/products"
                as={Link}
              >
                View All
              </Button>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category, index) => (
              <Reveal key={category.id} animation="scale" delay={index * 50}>
                <Link
                  to={`/products?category=${category.id}`}
                  className="group"
                >
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-3">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-semibold">{category.name}</h3>
                      <p className="text-white/70 text-sm">{category.count} products</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                Featured Products
              </h2>
              <p className="text-gray-300 dark:text-gray-500 max-w-xl mx-auto transition-colors duration-300">
                Handpicked selection of our best selling fresh products
              </p>
            </div>
          </Reveal>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-dark dark:bg-white rounded-xl p-4 shadow-sm transition-colors duration-300">
                  <div className="aspect-square bg-dark-light dark:bg-gray-100 rounded-lg mb-4 transition-colors duration-300" />
                  <div className="h-4 bg-dark-light dark:bg-gray-100 rounded mb-2 transition-colors duration-300" />
                  <div className="h-6 bg-dark-light dark:bg-gray-100 rounded w-2/3 transition-colors duration-300" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 4).map((product, index) => (
                <Reveal key={product.id} animation="slide-up" delay={index * 100}>
                  <ProductCard product={product} />
                </Reveal>
              ))}
            </div>
          )}

          <Reveal animation="fade-in" delay={400}>
            <div className="text-center mt-12">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
                to="/products"
                as={Link}
              >
                View All Products
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-r from-accent to-primary rounded-3xl overflow-hidden transition-all duration-300">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            
            <div className="relative z-10 px-8 py-12 md:px-16 md:py-16">
              <div className="max-w-2xl">
                <span className="inline-block bg-white/20 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                  Limited Time Offer
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Get 20% Off All Organic Vegetables
                </h2>
                <p className="text-white/80 text-lg mb-8">
                  Use code <strong>ORGANIC20</strong> at checkout. Valid until end of month.
                </p>
                <Button
                  variant="secondary"
                  size="lg"
                  to="/products?category=vegetables"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  as={Link}
                >
                  Shop Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                How It Works
              </h2>
              <p className="text-gray-300 dark:text-gray-500 max-w-xl mx-auto transition-colors duration-300">
                Getting fresh farm produce has never been easier
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Browse', description: 'Explore our selection of fresh farm products' },
              { step: '02', title: 'Select', description: 'Choose your favorite organic products' },
              { step: '03', title: 'Order', description: 'Place your order with secure payment' },
              { step: '04', title: 'Receive', description: 'Get fresh produce delivered to your door' },
            ].map((item, index) => (
              <Reveal key={index} animation="slide-up" delay={index * 100}>
                <div className="relative text-center">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-accent dark:from-primary to-transparent transition-colors duration-300" />
                  )}
                  <div className="relative z-10 w-16 h-16 mx-auto mb-4 bg-accent dark:bg-primary rounded-full flex items-center justify-center transition-colors duration-300">
                    <span className="text-white font-bold text-xl">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 dark:text-gray-500 text-sm transition-colors duration-300">
                    {item.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                What Our Customers Say
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Sarah M.',
                role: 'Health Enthusiast',
                image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
                content: 'The vegetables are incredibly fresh! You can really taste the difference between store-bought and farm-fresh produce.',
                rating: 5,
              },
              {
                name: 'John D.',
                role: 'Busy Father',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
                content: 'Convenient delivery and amazing quality. My kids actually want to eat their vegetables now!',
                rating: 5,
              },
              {
                name: 'Emma K.',
                role: 'Restaurant Owner',
                image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
                content: 'We source all our produce from Angel Cite. Consistent quality and reliable delivery for our restaurant.',
                rating: 5,
              },
            ].map((testimonial, index) => (
              <Reveal key={index} animation="slide-up" delay={index * 100}>
                <Card padding="lg" className="relative">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 dark:text-gray-600 leading-relaxed mb-6 transition-colors duration-300">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-gray-300 dark:text-gray-500 transition-colors duration-300">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark dark:bg-primary transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal animation="slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Taste the Difference?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Join hundreds of happy customers enjoying fresh, organic farm produce delivered to their doorstep.
            </p>
          </Reveal>
          <Reveal animation="fade-in" delay={200}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                as={Link}
                to="/products"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Start Shopping
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10"
                as={Link}
                to="/contact"
              >
                Contact Us
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
